import { useRef } from 'react';
import QRCode from 'react-qr-code';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { RegFieldsEnum, RegistrationQuestion } from '@/types/registration.types';
import { deriveRegistrationUpdate } from '../../helpers/derive-registration-update';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import SettingsToggle from '../../../../controls/settings-toggle';
import { ESettingsAdvancedWidgetLabels } from '../../../../settings.types';
import useLoadRegistrationQuestions from '@/connection/hooks/use-load-registration-questions';
import { getRegistrationQuestions } from '@/store/slices/channel/selectors';
import {
  selectIsSavingWorkingProject,
  selectProjectName,
  selectWorkingProjectDefaultLanguage,
  selectWorkingProjectRegistrationQuestions,
  selectWorkingProjectRequiredRegistrationQuestions,
  selectWorkingProjectUuid,
} from '@/store/slices/project/selectors';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import OptionalComponent from '@ui/optional-component/optional-component';
import TextCopyBox from '../../../integrations/components/inbound/text-copy-box/text-copy-box';
import { inPersonAttendeeModeBlockReason } from '../../helpers/can-use-in-person-attendee-mode';
import useGetProjectPublishedStatus from '@/connection/projects/use-get-project-published-status';
import { getPublishedStatus } from '@/store/slices/project/selectors';
import { selectInPersonAutoFill } from '@/store/slices/feature-flags/selectors';
import getProjectOverChannelColorCssVariable from '@/utils/colors/get-project-over-channel-color-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import useLanguageParams from '@/utils/use-language-params';

const QR_DOWNLOAD_SIZE = 1024;
// Availability is Brandlive-controlled, so this deliberately suggests no admin-side action.
const UNAVAILABLE_MESSAGE = "In-person attendee mode isn't available yet.";
const REGISTRATION_OFF_MESSAGE = 'Turn on registration to use in-person attendee mode.';

export default function InPersonAttendeeModeToggle() {
  useLoadRegistrationQuestions();
  useGetProjectPublishedStatus();
  const dispatch = useAppDispatch();

  const qrSourceRef = useRef<HTMLElement>(null);

  const projectName = useAppSelector(selectProjectName);
  const projectUuid = useAppSelector(selectWorkingProjectUuid);
  const publishedStatus = useAppSelector(getPublishedStatus);
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);
  const registrationQuestions = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const { registrationSettings, registrationSteps, registrationOn, singleSignOnEnabled } =
    useComputedRegistrationSettings();
  const autoFillEnabled = useAppSelector(selectInPersonAutoFill);
  const channelRegistrationQuestions = useAppSelector(getRegistrationQuestions);
  const requiredRegistrationQuestions = useAppSelector(selectWorkingProjectRequiredRegistrationQuestions);

  const defaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const paramsLaguage = useLanguageParams();
  const language = paramsLaguage || defaultLanguage;

  const inPersonAttendeeModeIsOn = registrationSettings?.adminInPersonAttendeeModeEnabled;
  const blockReason = autoFillEnabled ? inPersonAttendeeModeBlockReason(registrationSettings, registrationOn) : null;

  // Gate turning it on, never turning it off: the flag-off branch blocks SSO while the flag-on
  // branch allows it, so rolling the flag back would otherwise strand an on toggle with no undo.
  const cannotEnable = autoFillEnabled ? !!blockReason : singleSignOnEnabled;
  const isDisabled = savingWorkingProject || !registrationSettings || (cannotEnable && !inPersonAttendeeModeIsOn);

  let cannotEnableReason: string | undefined;
  if (cannotEnable && !inPersonAttendeeModeIsOn) {
    if (!autoFillEnabled) {
      cannotEnableReason = UNAVAILABLE_MESSAGE;
    } else if (blockReason === 'registration-off') {
      cannotEnableReason = REGISTRATION_OFF_MESSAGE;
    }
  }

  const isPublished = !!publishedStatus?.url;
  const regPath = language ? `/${language}/registration` : '';
  const inPersonLink = projectUuid
    ? `${import.meta.env.VITE_URL_SHORTENER}/${projectUuid}${regPath}?in-person=true`
    : '';
  const showInPersonLink = autoFillEnabled && !!inPersonAttendeeModeIsOn && !!inPersonLink;

  const projectSlug = (projectName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const qrFileName = `${projectSlug || 'project'}-in-person-qr.png`;

  // Rasterize the hidden SVG so on-site staff get a file they can drop straight into signage.
  const downloadQrCode = () => {
    const svg = qrSourceRef.current?.querySelector('svg');
    if (!svg) return;

    const svgBlob = new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(svgUrl);

      const canvas = document.createElement('canvas');
      canvas.width = QR_DOWNLOAD_SIZE;
      canvas.height = QR_DOWNLOAD_SIZE;

      const context = canvas.getContext('2d');
      if (!context) return;

      context.drawImage(image, 0, 0, QR_DOWNLOAD_SIZE, QR_DOWNLOAD_SIZE);

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = qrFileName;
      link.click();
    };

    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      toastTrigger('Could not download QR code', { type: EToastTypes.error });
    };

    image.src = svgUrl;
  };

  const handleToggle = (_key: string, on: boolean) => {
    if (!registrationSettings || !registrationQuestions) return;

    const hasQuestion = channelRegistrationQuestions.some(
      (question: RegistrationQuestion) => question.registration_question === RegFieldsEnum.attend_in_person
    );

    if (!hasQuestion) {
      toastTrigger('There was an error toggling in-person attendee mode', {
        type: EToastTypes.error,
        autoClose: 10000,
        description: 'Please refresh and try again later',
      });
      return;
    }

    // Routed through the shared derive so the question is reconciled by the same code an auth-flow
    // change runs, rather than by a second writer that has to agree with it.
    const update = deriveRegistrationUpdate(
      {
        registration_settings: registrationSettings,
        registration_steps: registrationSteps,
        registration_questions: registrationQuestions,
        required_registration_questions: requiredRegistrationQuestions,
      },
      { kind: 'in-person', on },
      channelRegistrationQuestions
    );

    dispatch(saveWorkingProjectByFields(update));
  };

  return (
    <SettingsToggle
      tooltipText={
        autoFillEnabled
          ? 'Adds an in-person option to your registration form and hides the video player for attendees who choose it. Share the in-person link for on-site signage or QR codes.'
          : 'Shows a checkbox on the registration page that hides the video player for users that want to attend your session in person'
      }
      switchTooltipText={cannotEnableReason}
      label={ESettingsAdvancedWidgetLabels.IN_PERSON_ATTENDEE_MODE}
      sKey="inPersonAttendeeMode"
      value={!!inPersonAttendeeModeIsOn}
      disabled={isDisabled}
      onToggle={handleToggle}
    >
      <OptionalComponent display={showInPersonLink}>
        <Container padding="0 16px 16px" className="flex fd-column gap-8">
          <Text className="P2" color="var(--global-ico-text-2)">
            Share this link — or download this{' '}
            <Button clear disabled={!isPublished} onClick={downloadQrCode}>
              <Text
                className="text-decoration-underline"
                color={getProjectOverChannelColorCssVariable(EColorVariablesV2.primary, 'global-cont-1')}
              >
                QR code
              </Text>
            </Button>{' '}
            — so on-site attendees skip the video player automatically.
          </Text>

          <Container className="display-none" ref={qrSourceRef}>
            <QRCode value={inPersonLink} size={QR_DOWNLOAD_SIZE} level="M" />
          </Container>

          <TextCopyBox label="In-person link" textToCopy={inPersonLink} disabled={!isPublished} />
          <OptionalComponent display={!isPublished}>
            <Text className="P2" color="var(--global-ico-text-2)">
              Publish your project to copy this link.
            </Text>
          </OptionalComponent>
        </Container>
      </OptionalComponent>
    </SettingsToggle>
  );
}
